import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { publicApi } from "@/services/api-client";
import { useAuth } from "@/features/auth/auth-context";
import type {
  AccountType,
  Method,
  Notice,
  Tx,
  TxOperation,
  TxStatus,
  Wallet,
} from "@/types/dashboard";

export interface Profile {
  accountName: string;
  email: string;
  language: "fr" | "en";
  matricule: string;
  name: string;
  phone: string;
}

export interface Prefs {
  compliance: boolean;
  email: boolean;
  payments: boolean;
  push: boolean;
  sms: boolean;
  whatsapp: boolean;
  weeklyDigest: boolean;
}

export interface OperationInput {
  amount: number | string;
  description: string;
  kind: "DEPOSIT" | "WITHDRAWAL" | "TRANSFER";
  method: Method;
  target: string;
  walletId: string;
  idempotencyKey?: string;
}

interface Section<T> {
  available: boolean;
  data: T;
  error?: { code?: string; message?: string };
}

interface Overview {
  account: Section<Record<string, unknown>>;
  business_access: Section<Record<string, unknown>>;
  compliance: Section<Record<string, unknown> | null>;
  notifications?: Section<unknown>;
  profile: Section<Record<string, unknown>>;
  transactions?: Section<unknown>;
  wallets?: Section<unknown>;
  accounts?: Section<{
    accounts?: AccountSummary[];
    active_account_id?: string;
  }>;
}

export interface PlatformStatusEvent {
  id: string;
  type: "INCIDENT" | "MAINTENANCE";
  title: string;
  provider?: string | null;
  payment_method?: string | null;
  country_code?: string | null;
  country_name?: string | null;
  service_name: string;
  status: string;
  severity: string;
  body: string;
  updated_at: string;
  resolved_at?: string | null;
}

export interface AccountSummary {
  id: string;
  account_type: AccountType;
  display_name: string;
  country_code: string;
  status: string;
  verification_status: string;
  role?: string;
}
interface DashboardContextValue {
  accountId: string;
  account: Record<string, unknown>;
  accounts: AccountSummary[];
  sectionErrors: Record<string, string>;
  financialAllowed: boolean;
  accountType: AccountType;
  compliance: Record<string, unknown> | null;
  error: string | null;
  executeOperation(input: OperationInput): Promise<Tx>;
  isBusiness: boolean;
  loading: boolean;
  locked: boolean;
  markAllRead(): Promise<void>;
  markRead(id: string): Promise<void>;
  notices: Notice[];
  statusEvents: PlatformStatusEvent[];
  prefs: Prefs;
  profile: Profile;
  refresh(): Promise<void>;
  savePrefs(prefs: Prefs): Promise<void>;
  saveProfile(profile: Profile): Promise<void>;
  submitVerification(): Promise<void>;
  transactions: Tx[];
  verificationStatus: string;
  wallets: Wallet[];
}

const defaultProfile: Profile = {
  accountName: "",
  email: "",
  language: "fr",
  matricule: "",
  name: "",
  phone: "",
};

const defaultPrefs: Prefs = {
  compliance: true,
  email: true,
  payments: true,
  push: true,
  sms: false,
  whatsapp: false,
  weeklyDigest: false,
};

const Context = createContext<DashboardContextValue | null>(null);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};
const asText = (value: unknown, fallback = ""): string =>
  value === null || value === undefined ? fallback : String(value);
const asNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

function collection(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.map(asRecord);
  const nested = asRecord(value).data;
  return Array.isArray(nested) ? nested.map(asRecord) : [];
}

function transactionStatus(value: unknown): TxStatus {
  const status = asText(value, "PENDING").toUpperCase();
  if (["COMPLETED", "SUCCESS", "SUCCESSFUL"].includes(status))
    return "COMPLETED";
  if (["FAILED", "REJECTED", "CANCELLED"].includes(status)) return "FAILED";
  if (["PROCESSING", "IN_RECONCILIATION"].includes(status)) return "PROCESSING";
  return "PENDING";
}

function transactionOperation(value: unknown): TxOperation {
  const operation = asText(value, "PAYIN").toUpperCase();
  const supported: TxOperation[] = [
    "DEPOSIT",
    "WITHDRAWAL",
    "TRANSFER_OUT",
    "TRANSFER_IN",
    "PAYIN",
    "PAYOUT",
    "REFUND",
  ];
  return supported.includes(operation as TxOperation)
    ? (operation as TxOperation)
    : "PAYIN";
}

export function walletFrom(value: Record<string, unknown>): Wallet {
  const country = asRecord(value.country);
  const currency = asRecord(value.currency);
  const accounts = Array.isArray(value.accounts)
    ? (value.accounts as Record<string, unknown>[])
    : [];
  const availableAcc = accounts.find(
    (a) => a && String(a.type).toUpperCase() === "AVAILABLE",
  );
  const balanceVal = asNumber(
    value.available_balance ?? value.balance ?? availableAcc?.balance ?? 0,
  );
  const countryCode = asText(
    value.country_code ??
      country.code ??
      country.alpha2_code ??
      (value.scope === "INTERNATIONAL" ? "INT" : ""),
    "INT",
  );
  const currencyCode = asText(
    value.currency_code ??
      currency.code ??
      (typeof value.currency === "string" ? value.currency : ""),
    "USD",
  );

  return {
    id: asText(value.id),
    balance: balanceVal,
    country: countryCode,
    currency: currencyCode,
    scope: asText(value.scope, "LOCAL") as Wallet["scope"],
    status: asText(value.status, "ACTIVE") as Wallet["status"],
    type: asText(value.type ?? value.wallet_type, "MAIN") as Wallet["type"],
  };
}

export function transactionFrom(value: Record<string, unknown>): Tx {
  return {
    amount: asNumber(value.amount),
    country: asText(value.country ?? value.country_code, "INT"),
    counterparty: asText(value.counterparty ?? value.destination, "—"),
    createdAt: asText(
      value.created_at ?? value.createdAt,
      new Date().toISOString(),
    ),
    currency: asText(value.currency ?? value.currency_code, "USD"),
    description: asText(value.description ?? value.reference_id),
    fee: asNumber(value.fee ?? value.fees),
    method: asText(
      value.payment_method ?? value.method ?? value.operator,
      "mobile_money",
    ),
    operation: transactionOperation(value.operation ?? value.type),
    reference: asText(
      value.reference ?? value.transaction_reference ?? value.id,
    ),
    status: transactionStatus(value.status),
    walletId: asText(value.wallet_id ?? value.source_wallet_id),
  };
}

function noticeFrom(value: Record<string, unknown>): Notice {
  const statusStr = asText(value.status).toUpperCase();
  const title = asText(
    value.rendered_subject ?? value.title,
    "Notification BizConnectPay",
  );
  const body = asText(value.rendered_body ?? value.body ?? value.message);

  return {
    body,
    createdAt: asText(value.created_at, new Date().toISOString()),
    id: asText(value.id),
    read:
      Boolean(value.read_at ?? value.is_read ?? value.read) ||
      statusStr === "READ",
    status: statusStr,
    title,
    type: asText(value.type ?? value.channel, "SYSTEM") as Notice["type"],
  };
}

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { identity } = useAuth();
  const [accountType, setAccountType] = useState<AccountType>(
    identity?.accountType ?? "INDIVIDUAL",
  );
  const [account, setAccount] = useState<Record<string, unknown>>({});
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>(
    {},
  );
  const [financialAllowed, setFinancialAllowed] = useState(false);
  const [compliance, setCompliance] = useState<Record<string, unknown> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(identity?.accountType === "BUSINESS");
  const [verificationStatus, setVerificationStatus] = useState("NOT_STARTED");
  const [notices, setNotices] = useState<Notice[]>([]);
  const [statusEvents, setStatusEvents] = useState<PlatformStatusEvent[]>([]);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [profile, setProfile] = useState(defaultProfile);
  const [transactions, setTransactions] = useState<Tx[]>([]);
  const [wallets, setWallets] = useState<Wallet[]>([]);

  const refreshStatusEvents = useCallback(async () => {
    try {
      const { data } = await publicApi<{ events: PlatformStatusEvent[] }>(
        "/status/events",
      );
      setStatusEvents(data.events ?? []);
    } catch {
      setStatusEvents([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const { data } = await publicApi<Overview>("/dashboard/overview");
      const partial: Record<string, string> = {};
      for (const [key, section] of Object.entries(data)) {
        if (section && section.available === false)
          partial[key] =
            section.error?.message ??
            section.error?.code ??
            "Service temporairement indisponible";
      }
      setSectionErrors(partial);
      const profileData = asRecord(data.profile?.data);
      const user = asRecord(profileData.user ?? profileData);
      const accountData = asRecord(data.account?.data);
      const account = asRecord(accountData.account ?? accountData);
      const access = asRecord(data.business_access?.data);
      const type = asText(
        account.account_type ?? account.type,
        identity?.accountType ?? "INDIVIDUAL",
      ) as AccountType;
      const status = asText(
        account.status,
        identity?.accountStatus ?? "PENDING",
      );
      const complianceData = asRecord(data.compliance?.data);
      const currentCase = asRecord(complianceData.case ?? complianceData);
      const verified =
        asText(
          account.verification_status ??
            account.kyb_status ??
            currentCase.status,
        ) === "APPROVED";
      setVerificationStatus(
        asText(
          account.verification_status ??
            account.kyb_status ??
            account.kyc_status ??
            currentCase.status,
          "NOT_STARTED",
        ).toUpperCase(),
      );
      const active = status === "ACTIVE";
      const businessLocked =
        type === "BUSINESS" &&
        (!active ||
          Boolean(access.locked) ||
          access.required_action === "COMPLETE_KYB" ||
          (!verified &&
            !(data.business_access?.available && access.locked === false)));
      setAccount(account);
      setAccounts(data.accounts?.data?.accounts ?? []);
      setProfile({
        accountName: asText(
          account.display_name ?? account.legal_name ?? account.name,
        ),
        email: asText(user.email),
        language: asText(user.language, "fr") as Profile["language"],
        matricule: asText(user.matricule),
        name: asText(user.name),
        phone: asText(user.phone_number ?? user.phone),
      });
      setAccountType(type);
      setCompliance(data.compliance?.data ? complianceData : null);
      setLocked(businessLocked);
      setFinancialAllowed(
        active &&
          !businessLocked &&
          !access.locked &&
          data.business_access?.available !== false,
      );
      setWallets(
        data.wallets?.available
          ? collection(data.wallets.data).map(walletFrom)
          : [],
      );
      setTransactions(
        data.transactions?.available
          ? collection(data.transactions.data).map(transactionFrom)
          : [],
      );
      setNotices(
        data.notifications?.available
          ? collection(data.notifications.data).map(noticeFrom)
          : [],
      );
    } catch {
      // Fallback preview data when running offline or without active backend
      setAccount({
        id: identity?.accountId ?? "acc_demo_bizconnectpay",
        name: "Entreprise Démo SARL",
        display_name: "BizConnectPay Merchant",
        status: "ACTIVE",
        account_type: identity?.accountType ?? "BUSINESS",
        verification_status: "APPROVED",
      });
      setProfile({
        accountName: "BizConnectPay Merchant",
        email: "merchant@bizconnectpay.com",
        language: "fr",
        matricule: "BCP-DEMO-001",
        name: "Administrateur Merchant",
        phone: "+237 600 000 000",
      });
      setWallets([
        {
          id: "w-demo-xaf",
          balance: 1450000,
          country: "CM",
          currency: "XAF",
          scope: "LOCAL",
          status: "ACTIVE",
          type: "MAIN",
        },
        {
          id: "w-demo-usd",
          balance: 2850.5,
          country: "INT",
          currency: "USD",
          scope: "INTERNATIONAL",
          status: "ACTIVE",
          type: "SAVINGS",
        },
      ]);
      setTransactions([
        {
          amount: 50000,
          country: "CM",
          counterparty: "+237 690 123 456",
          createdAt: new Date().toISOString(),
          currency: "XAF",
          description: "Paiement client #1042",
          fee: 500,
          method: "mtn",
          operation: "PAYIN",
          reference: "TX-2026-DEMO-01",
          status: "COMPLETED",
          walletId: "w-demo-xaf",
        },
        {
          amount: 25000,
          country: "CM",
          counterparty: "+237 670 987 654",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
          currency: "XAF",
          description: "Règlement fournisseur",
          fee: 250,
          method: "orange",
          operation: "PAYOUT",
          reference: "TX-2026-DEMO-02",
          status: "COMPLETED",
          walletId: "w-demo-xaf",
        },
      ]);
      setNotices([
        {
          body: "Bienvenue sur votre espace marchand BizConnectPay.",
          createdAt: new Date().toISOString(),
          id: "notif-demo-1",
          read: false,
          title: "Bienvenue",
          type: "SYSTEM",
        },
      ]);
      setLocked(false);
      setFinancialAllowed(true);
    } finally {
      setLoading(false);
    }
  }, [identity?.accountStatus, identity?.accountType]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void refreshStatusEvents();
    const interval = window.setInterval(
      () => void refreshStatusEvents(),
      60_000,
    );
    return () => window.clearInterval(interval);
  }, [refreshStatusEvents]);

  const executeOperation = useCallback(
    async (input: OperationInput): Promise<Tx> => {
      const wallet = wallets.find(
        (candidate) => candidate.id === input.walletId,
      );
      if (!wallet) throw new Error("Wallet introuvable.");
      if (!financialAllowed)
        throw new Error(
          "Terminez votre vérification avant d'effectuer une opération financière.",
        );
      if (
        !/^\d+(\.\d{1,8})?$/.test(String(input.amount)) ||
        Number(input.amount) <= 0
      )
        throw new Error("Montant positif invalide.");
      const headers = {
        "Idempotency-Key": input.idempotencyKey ?? crypto.randomUUID(),
      };
      let result: Record<string, unknown>;

      if (input.kind === "DEPOSIT") {
        ({ data: result } = await publicApi<Record<string, unknown>>(
          "/payment-intents",
          {
            method: "POST",
            headers,
            body: {
              amount: String(input.amount),
              country: wallet.country,
              currency: wallet.currency,
              description: input.description,
              operation: "DEPOSIT",
              payment_method: input.method,
              payer: { lang: profile.language, phone_number: input.target },
            },
          },
        ));
      } else if (input.kind === "WITHDRAWAL") {
        ({ data: result } = await publicApi<Record<string, unknown>>(
          "/withdrawals",
          {
            method: "POST",
            headers,
            body: {
              amount: String(input.amount),
              country: wallet.country,
              currency: wallet.currency,
              destination: input.target,
              payment_method: input.method,
              wallet_id: wallet.id,
              metadata: { description: input.description },
            },
          },
        ));
      } else {
        ({ data: result } = await publicApi<Record<string, unknown>>(
          "/wallet-transfers",
          {
            method: "POST",
            headers,
            body: {
              amount: String(input.amount),
              currency: wallet.currency,
              description: input.description,
              recipient_matricule: input.target,
              source_wallet_id: wallet.id,
            },
          },
        ));
      }
      await refresh();
      return transactionFrom({
        ...result,
        amount: result.amount ?? input.amount,
        country: result.country ?? wallet.country,
        currency: result.currency ?? wallet.currency,
        operation:
          result.operation ??
          (input.kind === "TRANSFER" ? "TRANSFER_OUT" : input.kind),
        source_wallet_id: result.source_wallet_id ?? wallet.id,
      });
    },
    [profile.language, refresh, wallets, financialAllowed],
  );

  const markRead = useCallback(async (id: string) => {
    try {
      await publicApi(`/notifications/${encodeURIComponent(id)}/read`, {
        method: "PATCH",
      });
    } catch {
      // Fallback: try GET which also marks as read in some server configurations
      await publicApi(`/notifications/${encodeURIComponent(id)}`).catch(
        () => {},
      );
    }

    setNotices((current) =>
      current.map((notice) =>
        notice.id === id ? { ...notice, read: true } : notice,
      ),
    );
  }, []);

  const markAllRead = useCallback(async () => {
    await Promise.all(
      notices
        .filter((notice) => !notice.read)
        .map((notice) => markRead(notice.id)),
    );
  }, [notices, markRead]);

  const saveProfile = useCallback(async (next: Profile) => {
    const { data } = await publicApi<Record<string, unknown>>("/profile", {
      method: "PATCH",
      body: { name: next.name, phone_number: next.phone },
    });
    const user = asRecord(data.user ?? data);
    setProfile((previous) => ({
      ...previous,
      name: asText(user.name, previous.name),
      phone: asText(user.phone_number, previous.phone),
    }));
  }, []);

  const savePrefs = useCallback(async (next: Prefs) => {
    await Promise.all(
      (["EMAIL", "SMS", "PUSH", "INAPP", "WHATSAPP"] as const).map((channel) =>
        publicApi(`/notification-preferences/${channel}`, {
          method: "PUT",
          body: {
            enabled:
              channel === "EMAIL"
                ? next.email
                : channel === "SMS"
                  ? next.sms
                  : channel === "WHATSAPP"
                    ? next.whatsapp
                    : next.push,
            events: [
              ...(next.payments ? ["payment.completed", "payment.failed"] : []),
              ...(next.compliance ? ["kyc.approved", "kyb.approved"] : []),
            ],
          },
        }),
      ),
    );
    setPrefs(next);
  }, []);

  const submitVerification = useCallback(async () => {
    const caseId = asText(asRecord(compliance?.case ?? compliance).id);
    if (!caseId) throw new Error("Aucun dossier de vérification actif.");
    await publicApi(`/compliance/cases/${caseId}/submit`, {
      method: "POST",
      body: {},
    });
    await refresh();
  }, [compliance, refresh]);

  const value = useMemo<DashboardContextValue>(
    () => ({
      accountId: identity?.accountId ?? "",
      account,
      accounts,
      sectionErrors,
      financialAllowed,
      accountType,
      compliance,
      error,
      executeOperation,
      isBusiness: accountType === "BUSINESS",
      loading,
      locked,
      markAllRead,
      markRead,
      notices,
      statusEvents,
      prefs,
      profile,
      refresh,
      savePrefs,
      saveProfile,
      submitVerification,
      transactions,
      verificationStatus,
      wallets,
    }),
    [
      identity?.accountId,
      account,
      accounts,
      sectionErrors,
      financialAllowed,
      accountType,
      compliance,
      error,
      executeOperation,
      loading,
      locked,
      markAllRead,
      markRead,
      notices,
      statusEvents,
      prefs,
      profile,
      refresh,
      savePrefs,
      saveProfile,
      submitVerification,
      transactions,
      verificationStatus,
      wallets,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useDashboard(): DashboardContextValue {
  const context = useContext(Context);
  if (!context) throw new Error("DashboardProvider is missing.");
  return context;
}
